import React from 'react';

import type { ControlCondense } from '../../controls/shared/types';
import type { CSS } from '../../stitches.config';
import type { BadgeArrangement, BadgeEnds, BadgeProps, BadgeSize, BadgeVariant } from './Badge';
import { ControlProvider, useControlSize } from '../../controls/shared/context';
import { colors, darkThemeSelector, rem, styled } from '../../stitches.config';
import { BadgeContainer } from './Badge';
import { BadgeProvider } from './BadgeContext';

const resetChildSelector = `& ${BadgeContainer}:not(:first-child,:last-child)`;
const firstChildSelector = `& ${BadgeContainer}:first-child:not(:last-child)`;
const lastChildSelector = `& ${BadgeContainer}:last-child:not(:first-child)`;
const leadingChildSelector = `& ${BadgeContainer}:not(:last-child)`;
const trailingChildSelector = `& ${BadgeContainer}:not(:first-child)`;

// Round-capped like atto-svgs' Plus.svg, cropped to the glyph and with a heavier stroke.
const plusGlyph = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 7 7"><path d="M3.5.8v5.4M.8 3.5h5.4" stroke="#000" stroke-width="1.6" stroke-linecap="round"/></svg>',
)}")`;

const roundedSquare = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 4"><rect width="4" height="4" rx="1"/></svg>',
)}")`;

const solid = 'linear-gradient(#000, #000)';

const tiedGlyph: CSS = {
  content: '',
  position: 'absolute',
  top: '50%',
  right: 0,
  display: 'block',
  transform: 'translateY(-50%)',
  background: colors.bodyNeutralLight,
  maskImage: plusGlyph,
  maskRepeat: 'no-repeat',
  maskSize: '100% 100%',

  [darkThemeSelector]: {
    background: colors.bodyNeutralDark,
  },
};

// The leading badge draws the whole `+` (split halves leave a hairline at 1x), its box running on
// under the gap since Safari can't unclip a mask. Keep `glyph` and `hole` even: odd sizes put edges
// on half pixels, which Firefox at 1x snaps unevenly, knocking the `+` off-center.
const tiedSeam = (glyph: number, hole: number, inset: number) => {
  const overhang = `(${rem(glyph / 2)} + $space$1 / 2)`;
  const plusSize = `${rem(glyph)} ${rem(glyph)}`;
  const holeSize = `${rem(hole)} ${rem(hole)}`;
  const leadingSolidSize = `calc(100% - ${overhang}) 100%`;
  const leadingHole = `calc(100% + ${rem((hole - glyph) / 2)}) 50%`;
  const trailingHole = `calc(${rem(-hole / 2)} - $space$1 / 2) 50%`;

  return {
    [leadingChildSelector]: {
      paddingRight: `calc(${rem(inset)} + ${overhang})`,
      marginRight: `calc(-1 * ${overhang})`,

      '&::after': {
        width: rem(glyph),
        height: rem(glyph),
      },
    },

    [trailingChildSelector]: {
      paddingLeft: rem(inset),
    },

    [firstChildSelector]: {
      maskImage: `${plusGlyph}, ${solid}, ${roundedSquare}`,
      maskPosition: `right, left, ${leadingHole}`,
      maskSize: `${plusSize}, ${leadingSolidSize}, ${holeSize}`,
      maskComposite: 'add, subtract, add',
    },

    [lastChildSelector]: {
      maskImage: `${solid}, ${roundedSquare}`,
      maskPosition: `center, ${trailingHole}`,
      maskSize: `auto, ${holeSize}`,
      maskComposite: 'subtract, add',
    },

    [resetChildSelector]: {
      maskImage: `${plusGlyph}, ${solid}, ${roundedSquare}, ${roundedSquare}`,
      maskPosition: `right, left, ${trailingHole}, ${leadingHole}`,
      maskSize: `${plusSize}, ${leadingSolidSize}, ${holeSize}, ${holeSize}`,
      maskComposite: 'add, subtract, add, add',
    },
  };
};

const BadgeGroupContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',

  variants: {
    alignment: {
      start: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
      },
      end: {
        justifyContent: 'flex-end',
      },
    },
    ends: {
      card: {},
      pill: {},
    },
    relation: {
      joined: {
        [resetChildSelector]: {
          borderRadius: 0,
        },
      },
      separate: {},
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
      large: {},
    },
    tied: {
      true: {
        gap: '$1',

        [`& ${BadgeContainer}`]: {
          position: 'relative',
          maskRepeat: 'no-repeat',
        },

        [leadingChildSelector]: {
          '&::after': tiedGlyph,
        },
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      relation: 'separate',
      size: 'x-small',
      css: {
        gap: '$2',
      },
    },
    {
      relation: 'separate',
      size: 'small',
      css: {
        gap: '$4',
      },
    },
    {
      relation: 'separate',
      size: 'medium',
      css: {
        gap: '$6',
      },
    },
    {
      relation: 'separate',
      size: 'large',
      css: {
        gap: '$8',
      },
    },
    {
      ends: 'card',
      relation: 'joined',
      size: 'x-small',
      css: {
        [firstChildSelector]: {
          borderRadius: '$4 0 0 $4',
        },

        [lastChildSelector]: {
          borderRadius: '0 $4 $4 0',
        },
      },
    },
    {
      ends: 'card',
      relation: 'joined',
      size: 'small',
      css: {
        [firstChildSelector]: {
          borderRadius: '$6 0 0 $6',
        },

        [lastChildSelector]: {
          borderRadius: '0 $6 $6 0',
        },
      },
    },
    {
      ends: 'card',
      relation: 'joined',
      size: 'medium',
      css: {
        [firstChildSelector]: {
          borderRadius: '$8 0 0 $8',
        },

        [lastChildSelector]: {
          borderRadius: '0 $8 $8 0',
        },
      },
    },
    {
      ends: 'card',
      relation: 'joined',
      size: 'large',
      css: {
        [firstChildSelector]: {
          borderRadius: '$10 0 0 $10',
        },

        [lastChildSelector]: {
          borderRadius: '0 $10 $10 0',
        },
      },
    },
    {
      ends: 'pill',
      relation: 'joined',
      css: {
        [firstChildSelector]: {
          borderRadius: '99em 0 0 99em',
        },

        [lastChildSelector]: {
          borderRadius: '0 99em 99em 0',
        },
      },
    },
    {
      tied: true,
      size: 'x-small',
      css: tiedSeam(6, 8, 7),
    },
    {
      tied: true,
      size: 'small',
      css: tiedSeam(8, 10, 9),
    },
    {
      tied: true,
      size: 'medium',
      css: tiedSeam(8, 12, 11),
    },
    {
      tied: true,
      size: 'large',
      css: tiedSeam(10, 14, 12),
    },
  ],
});
BadgeGroupContainer.displayName = 'BadgeGroupContainer';

export type BadgeGroupProps = {
  children: React.ReactNode;
  arrangement?: BadgeArrangement;
  alignment?: 'start' | 'center' | 'end';
  /** Condenses descendant icon-bearing badges to their icons; see `Badge`'s `condense`. */
  condense?: boolean | ControlCondense;
  ends?: BadgeEnds;
  relation?: 'joined' | 'separate';
  size?: BadgeSize;
  tied?: boolean;
  variant?: BadgeVariant;
};

export type BadgeGroupItemProps = Omit<BadgeProps, 'size'>;

export type BadgeGroupRecipe = Omit<BadgeGroupProps, 'children'> & {
  items: BadgeGroupItemProps[];
};

export function BadgeGroup({
  children,
  arrangement,
  alignment = 'start',
  condense,
  ends = 'card',
  relation = 'separate',
  size: sizeProp,
  tied,
  variant,
  ...remaining
}: BadgeGroupProps) {
  const controlSize = useControlSize(sizeProp, 'medium');
  const size: BadgeSize = controlSize === 'x-large' ? 'large' : controlSize;

  return (
    <BadgeGroupContainer
      alignment={alignment}
      ends={ends}
      relation={relation}
      size={size}
      tied={tied}
      {...remaining}
    >
      <ControlProvider value={{ condense, size }}>
        <BadgeProvider value={{ arrangement, ends }}>{children}</BadgeProvider>
      </ControlProvider>
    </BadgeGroupContainer>
  );
}
